/**
 * DefencIQ Stack Mapper
 * Maps domain scores and question answers to recommended technology stack components
 * Groups components by layer
 */

import {
  COMPONENT_LABELS,
  LAYER_MAP,
  LAYER_LABELS
} from '@/config/domain-weights';
import {
  QUESTION_CONFIG,
  getAllQuestions
} from '@/config/question-config';
import { getAnswerScore } from './scoring-engine';

/**
 * Get recommended stack components based on question answers
 * If a question score is 0 or 1 and recommended_components exists, include those components
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Array} Unique recommended components
 */
export const getRecommendedComponents = (answers) => {
  const componentMap = {};
  const questions = getAllQuestions();

  questions.forEach(question => {
    const answer = answers[question.field_id];
    const score = getAnswerScore(answer, question);

    // If score is 0 or 1 and has recommended components, add them
    if (score < 2 && question.recommended_components) {
      question.recommended_components.forEach(comp => {
        if (!componentMap[comp]) {
          componentMap[comp] = { maxCriticality: null, minScore: 2 };
        }
        // Track the highest criticality and lowest score that triggered this component
        const critOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        const currentOrder = critOrder[componentMap[comp].maxCriticality] || 0;
        const newOrder = critOrder[question.criticality] || 0;
        if (newOrder > currentOrder) {
          componentMap[comp].maxCriticality = question.criticality;
        }
        if (score < componentMap[comp].minScore) {
          componentMap[comp].minScore = score;
        }
      });
    }
  });

  return Object.entries(componentMap).map(([id, meta]) => ({
    id,
    // Required: triggered by critical/high gap OR question scored 0 (not implemented)
    requirement: (meta.maxCriticality === 'critical' || meta.maxCriticality === 'high' || meta.minScore === 0)
      ? 'required' : 'recommended'
  }));
};

/**
 * Group components by layer
 * @param {Array} components - List of component IDs
 * @returns {Object} Components grouped by layer
 */
export const groupComponentsByLayer = (components) => {
  const grouped = {};
  // components is now array of { id, requirement }
  const compIds = components.map(c => typeof c === 'string' ? c : c.id);
  const reqMap = {};
  components.forEach(c => {
    if (typeof c === 'object') reqMap[c.id] = c.requirement;
  });

  Object.entries(LAYER_MAP).forEach(([layerId, layerComponents]) => {
    const matching = compIds.filter(c => layerComponents.includes(c));
    if (matching.length > 0) {
      grouped[layerId] = {
        label: LAYER_LABELS[layerId],
        components: matching.map(compId => ({
          id: compId,
          label: COMPONENT_LABELS[compId] || compId,
          requirement: reqMap[compId] || 'recommended'
        }))
      };
    }
  });

  return grouped;
};

/**
 * Get component label
 * @param {string} componentId - Component ID
 * @returns {string} Human-readable label
 */
export const getComponentLabel = (componentId) => {
  return COMPONENT_LABELS[componentId] || componentId;
};

/**
 * Get layer for a component
 * @param {string} componentId - Component ID
 * @returns {string|null} Layer ID or null
 */
export const getComponentLayer = (componentId) => {
  for (const [layerId, components] of Object.entries(LAYER_MAP)) {
    if (components.includes(componentId)) {
      return layerId;
    }
  }
  return null;
};

/**
 * Get full stack recommendation with layers
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Stack recommendation with summary
 */
export const getStackRecommendation = (answers) => {
  const recommendedComponents = getRecommendedComponents(answers);
  const groupedByLayer = groupComponentsByLayer(recommendedComponents);

  return {
    totalComponents: recommendedComponents.length,
    components: recommendedComponents.map(c => ({
      id: c.id,
      label: getComponentLabel(c.id),
      layer: getComponentLayer(c.id),
      requirement: c.requirement
    })),
    byLayer: groupedByLayer,
    summary: {
      enforcement: groupedByLayer.enforcement_layer?.components.length || 0,
      validation: groupedByLayer.validation_layer?.components.length || 0,
      monitoring: groupedByLayer.monitoring_layer?.components.length || 0,
      compliance: groupedByLayer.compliance_layer?.components.length || 0
    }
  };
};

/**
 * Determine greenfield candidate status
 * Greenfield if:
 * - identity_and_mfa domain score < 30
 * - endpoint_management domain score < 30
 * - application_control domain score < 20
 * - patch_management domain score < 30
 * @param {Object} domainScores - Domain scores
 * @returns {boolean} Is greenfield
 */
export const isGreenfieldEnvironment = (domainScores) => {
  const identityScore = domainScores.identity_and_mfa?.percentage || 0;
  const endpointScore = domainScores.endpoint_management?.percentage || 0;
  const appControlScore = domainScores.application_control?.percentage || 0;
  const patchScore = domainScores.patch_management?.percentage || 0;

  return (
    identityScore < 30 &&
    endpointScore < 30 &&
    appControlScore < 20 &&
    patchScore < 30
  );
};

// Keep legacy function for backward compatibility
export const isGreenfieldCandidate = (domainScores, answers) => {
  return isGreenfieldEnvironment(domainScores);
};

/**
 * Determine Environment Type (descriptive only - no service terminology)
 * Valid Environment Types:
 * - Greenfield: identity < 30, endpoint < 30, app_control < 20, patch < 30
 * - Fragmented / Ad Hoc: overall < 60 and not Greenfield
 * - Structured but Non-Compliant: overall between 60 and 84
 * - Mature / ML2-Aligned: overall >= 85
 * @param {number} overallScore - Overall percentage score
 * @param {Object} domainScores - Domain scores
 * @returns {string} Environment type
 */
export const determineEnvironmentType = (overallScore, domainScores) => {
  // Check for Greenfield first
  if (isGreenfieldEnvironment(domainScores)) {
    return 'greenfield';
  }
  
  // Mature / ML2-Aligned: overall >= 85
  if (overallScore >= 85) {
    return 'mature_aligned';
  }
  
  // Structured but Non-Compliant: overall between 60 and 84
  if (overallScore >= 60) {
    return 'structured_non_compliant';
  }
  
  // Fragmented / Ad Hoc: overall < 60 and not Greenfield
  return 'fragmented_adhoc';
};

/**
 * Get Environment Type display info
 * @param {string} type - Environment type ID
 * @returns {Object} Type info with label and description
 */
export const getEnvironmentTypeInfo = (type) => {
  const types = {
    greenfield: {
      label: 'Greenfield',
      description: 'Minimal security infrastructure in place. Requires full build-out.'
    },
    fragmented_adhoc: {
      label: 'Fragmented / Ad Hoc',
      description: 'Some tools and processes exist but lack consistency and integration.'
    },
    structured_non_compliant: {
      label: 'Structured but Non-Compliant',
      description: 'Established security practices but gaps remain for ML2 compliance.'
    },
    mature_aligned: {
      label: 'Mature / ML2-Aligned',
      description: 'Strong security posture closely aligned with Essential Eight ML2.'
    }
  };
  return types[type] || types.fragmented_adhoc;
};

/**
 * Check if organisation has existing foundations
 * @param {Object} answers - All answers
 * @returns {boolean} Has usable foundations
 */
const hasExistingFoundations = (answers) => {
  const hasIdentityPlatform = answers.identity_platform?.answer && 
    !['unknown', 'other'].includes(answers.identity_platform.answer);
  
  const hasDeviceMgmt = answers.central_device_management?.answer && 
    answers.central_device_management.answer !== 'none' &&
    answers.central_device_management.answer !== 'unknown';

  const hasSomeIT = answers.it_support_model?.answer && 
    answers.it_support_model.answer !== 'none';

  return hasIdentityPlatform || hasDeviceMgmt || hasSomeIT;
};

// Keep legacy function for backward compatibility
export const isManagedUpliftCandidate = (overallScore, answers) => {
  if (overallScore < 30 || overallScore >= 85) {
    return false;
  }
  return hasExistingFoundations(answers);
};

/**
 * Determine recommended Service Tier (prescriptive - commercial offering)
 * Valid Service Tiers:
 * - Snapshot Assessment: Low scores, discovery incomplete, or mature environments
 * - Managed ML2 Uplift: Mid-range scores with existing foundations
 * - DISP Ready Program: Higher scores needing compliance polish
 * - Greenfield ML2 Build: Greenfield environments requiring full build
 * @param {number} overallScore - Overall percentage score
 * @param {string} environmentType - Environment type
 * @param {Object} answers - All answers
 * @returns {string} Service tier
 */
export const determineServiceTier = (overallScore, greenfieldCandidate, managedUpliftCandidate, answers) => {
  // For backward compatibility, also accept environmentType as second param
  const isGreenfield = typeof greenfieldCandidate === 'boolean' 
    ? greenfieldCandidate 
    : greenfieldCandidate === 'greenfield';

  // Greenfield ML2 Build for greenfield environments
  if (isGreenfield) {
    return 'greenfield_build';
  }

  // Mature/Aligned environments typically need Snapshot Assessment (validation)
  if (overallScore >= 85) {
    return 'snapshot';
  }

  // DISP Ready Program for higher scores (60-84)
  if (overallScore >= 60) {
    return 'disp_ready';
  }

  // Check for audit/evidence support needs
  const needsAuditSupport = answers.evidence_collection_method?.answer === 'manual_documents' ||
    answers.evidence_collection_method?.answer === 'none';

  if (needsAuditSupport && overallScore >= 40) {
    return 'disp_ready';
  }

  // Managed ML2 Uplift for mid-range with foundations (25-59)
  const hasFoundations = hasExistingFoundations(answers);
  if (overallScore >= 25 && hasFoundations) {
    return 'managed_ml2';
  }

  // Snapshot Assessment for low scores or incomplete
  return 'snapshot';
};

/**
 * Get service tier info
 * @param {string} tier - Service tier ID
 * @returns {Object} Tier info with label and description
 */
export const getServiceTierInfo = (tier) => {
  const tiers = {
    snapshot: {
      label: 'Snapshot Assessment',
      description: 'Point-in-time assessment to identify gaps and priorities'
    },
    managed_ml2: {
      label: 'Managed ML2 Uplift',
      description: 'Structured program to uplift existing environment to ML2 compliance'
    },
    disp_ready: {
      label: 'DISP Ready Program',
      description: 'Focused program to achieve DISP membership readiness'
    },
    greenfield_build: {
      label: 'Greenfield ML2 Build',
      description: 'Full security stack design and implementation from the ground up'
    }
  };
  return tiers[tier] || tiers.snapshot;
};

export default {
  getRecommendedComponents,
  groupComponentsByLayer,
  getComponentLabel,
  getComponentLayer,
  getStackRecommendation,
  isGreenfieldCandidate,
  isGreenfieldEnvironment,
  isManagedUpliftCandidate,
  determineEnvironmentType,
  getEnvironmentTypeInfo,
  determineServiceTier,
  getServiceTierInfo
};
